import React from 'react';
import Box from '@mui/material/Box';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { useTranslation } from 'react-i18next';
import { dsV2 } from '@bloomays-lib/ui.shared';
import type { IContact } from '@bloomays-lib/types.shared';
import { SrcBadge } from './badges';

const AVA_TINTS = [dsV2.color.noir, '#5B5ED4', dsV2.color.bleu, '#B03080', dsV2.color.vert, '#7A7E1A'];
const tintOf = (seed = '') => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVA_TINTS[h % AVA_TINTS.length];
};
const initialsOf = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

/** Action d'icône d'une ligne contact (écrire / appeler en lien natif, modifier en bouton). */
const ContactRowAction = ({
  title,
  href,
  onClick,
  children,
}: {
  title: string;
  href?: string;
  onClick?: () => void;
  children: React.ReactNode;
}) => {
  const sharedSx = {
    width: 26,
    height: 26,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '7px',
    color: dsV2.color.t3,
    cursor: 'pointer',
    border: `1px solid ${dsV2.color.border}`,
    background: dsV2.color.blanc,
    transition: 'color .15s, border-color .15s, background .15s',
    '&:hover': { color: dsV2.color.bleu, borderColor: dsV2.color.bleu, background: dsV2.color.bleuDim },
  };
  if (href) {
    return (
      <Box
        component="a"
        href={href}
        title={title}
        aria-label={title}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        sx={{ ...sharedSx, textDecoration: 'none' }}
      >
        {children}
      </Box>
    );
  }
  return (
    <Box component="button" type="button" title={title} aria-label={title} onClick={onClick} sx={sharedSx}>
      {children}
    </Box>
  );
};

/** Légende des sources de contact (créa `.legend-bar`) : badges mission / fallback + note. */
export const ContactsLegend = () => {
  const { t } = useTranslation(['missionDashboard']);
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '11.5px',
    fontWeight: 600,
  } as const;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
      <Box component="span" sx={{ ...base, background: dsV2.color.bleuDim, color: dsV2.color.bleu }}>
        <AssignmentRoundedIcon sx={{ fontSize: 13 }} /> {t('legend.missionContact', { ns: 'missionDashboard' })}
      </Box>
      <Box
        component="span"
        sx={{
          ...base,
          background: dsV2.color.amberDim,
          color: dsV2.color.amber,
          border: `1px dashed ${dsV2.color.amberBrd}`,
        }}
      >
        <SyncRoundedIcon sx={{ fontSize: 13 }} /> {t('legend.societyFallback', { ns: 'missionDashboard' })}
      </Box>
      <Box sx={{ fontSize: 11, color: dsV2.color.t4, fontStyle: 'italic' }}>
        {t('legend.note', { ns: 'missionDashboard' })}
      </Box>
    </Box>
  );
};

/** Ligne contact (`.ce`) : avatar à teinte déterministe, nom, fonction, email + actions au survol. */
export const ContactRow = ({ contact, onEdit }: { contact: IContact; onEdit?: () => void }) => {
  const { t } = useTranslation(['missionDashboard']);
  const name =
    contact.fullname ||
    `${contact.firstname ?? ''} ${contact.lastname ?? ''}`.trim() ||
    t('contactRow.fallback', { ns: 'missionDashboard' });
  const hasActions = Boolean(contact.email || contact.phoneNumber || onEdit);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        padding: '10px 0',
        '&:hover .ce-actions': { opacity: 1 },
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          flexShrink: 0,
          borderRadius: '9px',
          background: tintOf(name),
          color: dsV2.color.blanc,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: dsV2.font.sansSemiBold,
          fontWeight: 600,
          fontSize: 34 * 0.32,
          letterSpacing: '0.02em',
        }}
      >
        {initialsOf(name)}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontSize: '12.5px', fontWeight: 600, lineHeight: 1.3, color: dsV2.color.noir }}>{name}</Box>
        {contact.nameJob ? (
          <Box
            sx={{
              fontSize: 11,
              color: dsV2.color.t4,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {contact.nameJob}
          </Box>
        ) : null}
        {contact.email ? (
          <Box
            sx={{
              fontSize: 11,
              color: dsV2.color.bleu,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {contact.email}
          </Box>
        ) : null}
      </Box>
      {hasActions ? (
        <Box
          className="ce-actions"
          sx={{
            display: 'flex',
            gap: '4px',
            flexShrink: 0,
            opacity: { xs: 1, md: 0 },
            transition: 'opacity .15s',
          }}
        >
          {contact.email ? (
            <ContactRowAction
              title={t('contactRow.write', { ns: 'missionDashboard' })}
              href={`mailto:${contact.email}`}
            >
              <MailOutlineRoundedIcon sx={{ fontSize: 14 }} />
            </ContactRowAction>
          ) : null}
          {contact.phoneNumber ? (
            <ContactRowAction
              title={t('contactRow.call', { ns: 'missionDashboard' })}
              href={`tel:${contact.phoneNumber}`}
            >
              <CallRoundedIcon sx={{ fontSize: 14 }} />
            </ContactRowAction>
          ) : null}
          {onEdit ? (
            <ContactRowAction title={t('contactRow.edit', { ns: 'missionDashboard' })} onClick={onEdit}>
              <EditRoundedIcon sx={{ fontSize: 14 }} />
            </ContactRowAction>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

/** Sous-bloc Bloomer (la personne freelance) — propre au panneau Fournisseur (créa `BloomerRow`). */
export const BloomerRow = ({
  name,
  subtitle,
  email,
  phoneNumber,
  onEdit,
}: {
  name: string;
  subtitle?: string;
  email?: string;
  phoneNumber?: string;
  onEdit?: () => void;
}) => {
  const { t } = useTranslation(['missionDashboard']);
  return (
    <Box sx={{ borderBottom: `1px solid ${dsV2.color.borderLight}` }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 18px',
          background: dsV2.color.surface2,
          borderBottom: `1px solid ${dsV2.color.borderLight}`,
        }}
      >
        <Box
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: dsV2.color.t3,
          }}
        >
          {t('bloomerRow.principal', { ns: 'missionDashboard' })}
        </Box>
        <Box sx={{ marginLeft: 'auto' }}>
          <SrcBadge source="mission" />
        </Box>
      </Box>
      <Box sx={{ padding: '0 18px', '&:hover .ce-actions': { opacity: 1 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '10px 0' }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              flexShrink: 0,
              borderRadius: '9px',
              background: tintOf(name),
              color: dsV2.color.blanc,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: dsV2.font.sansSemiBold,
              fontWeight: 600,
              fontSize: 34 * 0.32,
            }}
          >
            {initialsOf(name)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ fontSize: '12.5px', fontWeight: 600, lineHeight: 1.3, color: dsV2.color.noir }}>{name}</Box>
            {subtitle ? <Box sx={{ fontSize: 11, color: dsV2.color.t4 }}>{subtitle}</Box> : null}
            {email ? (
              <Box
                sx={{
                  fontSize: 11,
                  color: dsV2.color.bleu,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {email}
                {phoneNumber ? ` · ${phoneNumber}` : ''}
              </Box>
            ) : null}
          </Box>
          <Box
            className="ce-actions"
            sx={{ display: 'flex', gap: '4px', flexShrink: 0, opacity: { xs: 1, md: 0 }, transition: 'opacity .15s' }}
          >
            {email ? (
              <ContactRowAction title={t('contactRow.write', { ns: 'missionDashboard' })} href={`mailto:${email}`}>
                <MailOutlineRoundedIcon sx={{ fontSize: 14 }} />
              </ContactRowAction>
            ) : null}
            {phoneNumber ? (
              <ContactRowAction title={t('contactRow.call', { ns: 'missionDashboard' })} href={`tel:${phoneNumber}`}>
                <CallRoundedIcon sx={{ fontSize: 14 }} />
              </ContactRowAction>
            ) : null}
            {onEdit ? (
              <ContactRowAction title={t('contactRow.editBloomer', { ns: 'missionDashboard' })} onClick={onEdit}>
                <EditRoundedIcon sx={{ fontSize: 14 }} />
              </ContactRowAction>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
